import { MailOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase, { getAuthRedirectURL } from "../../config/supabaseConfig";

const { Title, Text } = Typography;

interface ForgotPasswordProps {
    closeModal?: () => void;
    goToLogin?: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ closeModal, goToLogin }) => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [sentEmail, setSentEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async () => {
        if (!email) {
            setError("L'email est requis");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: getAuthRedirectURL('auth/reset-password'),
            });

            if (error) throw error;

            setSuccess(true);
            setSentEmail(email);
        } catch (error) {
            setError(error instanceof Error ? error.message : "Une erreur est survenue");
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        if (goToLogin) {
            goToLogin();
        } else if (closeModal) {
            closeModal();
        } else {
            navigate('/auth/login');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`${closeModal ? '' : 'min-h-screen bg-gray-100'} flex items-center justify-center p-4`}
        >
            <Card className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Title level={3}>Réinitialiser votre mot de passe</Title>
                    <Text className="text-gray-500">
                        Entrez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe
                    </Text>
                </div>

                <Form layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        label="Email"
                        name="email"
                        rules={[
                            { required: true, message: 'Veuillez saisir votre email' },
                            { type: 'email', message: 'Veuillez saisir un email valide' }
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined className="site-form-item-icon" />}
                            placeholder="Entrez votre email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="w-full"
                            loading={loading}
                        >
                            Envoyer le lien de réinitialisation
                        </Button>
                    </Form.Item>

                    <div className="text-center mt-4">
                        <Button
                            type="link"
                            onClick={handleBackToLogin}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            Retour à la connexion
                        </Button>
                    </div>
                </Form>

                {error && (
                    <Alert
                        message="Erreur"
                        description={error}
                        type="error"
                        showIcon
                        className="mt-4"
                    />
                )}

                {success && (
                    <Alert
                        message="Email envoyé"
                        description={`Un email a été envoyé à ${sentEmail} avec les instructions pour réinitialiser votre mot de passe.`}
                        type="success"
                        showIcon
                        className="mt-4"
                    />
                )}
            </Card>
        </motion.div>
    );
};

export default ForgotPassword;